import * as React from 'react'
import { useDebouncedCallback } from 'use-debounce'

import {
  TFUserAgreement,
  TQUserAgreementsVariables,
  useQUserAgreementsLazyQuery,
} from '~graphql/generated/clientGraphql'
import { useAuthCtx, useAuthStateCtx } from '~logic/auth/AuthProvider'
import { useLazyQueryContext } from '~shared/helpers/useLazyQueryContext'
import { useFormContext, useWatch } from '~shared/segments/Form'

import { useCustomerAccountsEnabledCtx } from '../../CustomerAccounts'
import { useFormOrderCtx } from '../contexts/FormOrderProvider'
import { TOrderForm } from '../contexts/FormProvider'

// all of the info that might get the customer recognized
// so we want to refetch the agreements & disable those field
// he has already agreed with before
type TRefetchOptions = {
  email: TOrderForm['email']
  givenName: TOrderForm['givenName']
  familyName: TOrderForm['familyName']
  phonePrefix: TOrderForm['phonePrefix']
  phoneNumber: TOrderForm['phoneNumber']
  customerId: TQUserAgreementsVariables['customerId']
}

// do not refetch on every keystroke, waiting for some time should not be a problem here
const REFETCH_DEBOUNCE_MS = 1000

/**
 * Loads & reloads agreements that are associated with the order.
 * Also contains the state of the agreements from the past
 * (eg. which agreements did the customer already agreed with),
 * but NOT the current state as the customer is finishing this specific order
 * – the newly confirmed agreements are in the order form & will be submitted with it.
 */
export const useLoadAgreements = () => {
  const order = useFormOrderCtx()
  const { control } = useFormContext<TOrderForm>()
  const { areCustomerAccountsEnabled } = useCustomerAccountsEnabledCtx()

  const { isLoggedIn } = useAuthStateCtx()
  const { userId } = useAuthCtx()

  const [agreements, setAgreements] = React.useState<RoA<TFUserAgreement>>([])

  // ! keep the order of those two arrays in sync!
  const [
    givenName, //
    familyName,
    phonePrefix,
    phoneNumber,
    email,
  ] = useWatch({
    control,
    name: [
      `givenName`, //
      `familyName`,
      `phonePrefix`,
      `phoneNumber`,
      `email`,
    ],
  })

  const context = useLazyQueryContext()
  const [qAgreements] = useQUserAgreementsLazyQuery({ context })

  // refetch the agreements when some inputs changes to get customers past choices
  const refetchAgreements = React.useCallback(
    async ({
      email, //
      givenName,
      familyName,
      phonePrefix,
      phoneNumber,
      customerId,
    }: TRefetchOptions) => {
      if (!order) {
        return
      }

      const phone = `${phonePrefix.trim()}${phoneNumber.trim()}`

      const variables: TQUserAgreementsVariables = {
        email,
        phone,
        givenName,
        familyName,
        customerId,
        orderId: order.id,
      }

      const { data, error } = await qAgreements({ variables })

      if (!data || error) return

      setAgreements(data.agreements)
    },
    [order, qAgreements],
  )

  // refetch only after some delay in changes
  const debouncedRefetch = useDebouncedCallback(refetchAgreements, REFETCH_DEBOUNCE_MS)

  // the effect actually calling the debounced callback on value change
  React.useEffect(() => {
    const isCustomer = areCustomerAccountsEnabled && isLoggedIn

    debouncedRefetch({
      email,
      givenName,
      familyName,
      phonePrefix,
      phoneNumber,
      customerId: isCustomer ? userId : null,
    })
  }, [
    areCustomerAccountsEnabled,
    debouncedRefetch,
    email,
    familyName,
    givenName,
    isLoggedIn,
    phoneNumber,
    phonePrefix,
    userId,
  ])

  // return the most current state of the agreements
  return agreements
}
